/**
 * Stashed flow tokens — shared storage with a lifetime.
 *
 * The claim / seeker / invite flows all park a token in localStorage so it
 * survives the Supabase email-OTP signup hop (sessionStorage does not — that is
 * why localStorage was chosen). What was missing was an END: a token stashed by
 * one visit lived forever and silently reshaped the NEXT, unrelated signup.
 *
 * Measured on 2026-07-30: a claim token stashed 2026-07-22 was redeemed on
 * 2026-07-30 by an account created 41 seconds earlier (maciek@flathero.pl), who
 * had never opened the claim link in that session. He got an owner account and a
 * flat he does not own, and never saw the role selector — it was not on the page.
 *
 * So every stashed token now carries the moment it was stashed and expires
 * after FLOW_TOKEN_TTL_MS. A legacy bare-string value (the old format) is
 * treated as expired and dropped: those values are exactly the poison being
 * removed, and re-opening the link re-stashes with a fresh clock.
 *
 * The expiry is a floor, not the whole fix — the visible fix is that the role
 * selector is never hidden again (see BasicInfoStep).
 */

/** How long a stashed flow token stays usable. One signup sitting, generously. */
export const FLOW_TOKEN_TTL_MS = 2 * 60 * 60 * 1000; // 2 h

interface StoredToken {
  /** the token itself */
  t: string;
  /** epoch ms when it was stashed */
  at: number;
}

function storage(): Storage | null {
  try {
    return typeof localStorage !== 'undefined' ? localStorage : null;
  } catch {
    // Safari private mode / blocked storage — degrade to "no stash".
    return null;
  }
}

/** Stash a token together with the moment it was stashed (restarts the clock). */
export function stashFlowToken(key: string, token: string): void {
  const s = storage();
  if (!s) return;
  try {
    s.setItem(key, JSON.stringify({ t: token, at: Date.now() } satisfies StoredToken));
  } catch {
    /* quota / blocked — the flow degrades to "no pending token" */
  }
}

/**
 * Read a stashed token, or null when it is absent, malformed, expired, or fails
 * the caller's shape check. Anything that returns null is also REMOVED, so a bad
 * value cannot keep being re-read on every page load.
 */
export function readFlowToken(
  key: string,
  isValid: (token: string | null | undefined) => boolean,
): string | null {
  const s = storage();
  if (!s) return null;

  const raw = s.getItem(key);
  if (raw == null) return null;

  const drop = () => {
    try {
      s.removeItem(key);
    } catch {
      /* ignore */
    }
    return null;
  };

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    // Legacy bare-string value (pre-TTL format) — treat as expired.
    return drop();
  }

  if (
    typeof parsed !== 'object' ||
    parsed === null ||
    typeof (parsed as StoredToken).t !== 'string' ||
    typeof (parsed as StoredToken).at !== 'number'
  ) {
    return drop();
  }

  const { t, at } = parsed as StoredToken;
  const age = Date.now() - at;
  // age < 0 → the clock moved backwards (or the value was hand-written); the
  // stamp cannot be trusted, so the token cannot be trusted either.
  if (age < 0 || age > FLOW_TOKEN_TTL_MS) return drop();
  if (!isValid(t)) return drop();

  return t;
}

export function clearFlowToken(key: string): void {
  const s = storage();
  if (!s) return;
  try {
    s.removeItem(key);
  } catch {
    /* ignore */
  }
}
